import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'

import { cn } from '@/lib/utils'

/**
 * Button — shadcn/ui-style variants on top of `cva`.
 *
 * Differences with the driver-PWA `Button`:
 *   - Desktop sizes — `sm` / `md` / `lg` instead of `md` / `lg`.
 *     `md` (40px) is the default touch target on desktop, `lg`
 *     (44px) is for primary CTAs in dialogs, `sm` (32px) is for
 *     dense table rows.
 *   - Variant set matches shadcn so future ports of components
 *     stay copy-paste compatible.
 *
 * No `asChild` (Radix Slot) for now — the routes the admin needs
 * to wrap with button styling all use a plain `<button onClick>`
 * pattern. We'll add `@radix-ui/react-slot` and an `asChild`
 * prop the day a `<Link>` needs button styling.
 */

export const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 rounded-md font-medium',
    'transition-colors duration-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
    'disabled:opacity-60 disabled:cursor-not-allowed',
  ),
  {
    variants: {
      variant: {
        primary:
          'bg-brand text-brand-fg hover:bg-brand-strong active:bg-brand-strong shadow-sm',
        secondary:
          'bg-surface text-text border border-surface-border hover:bg-surface-hover active:bg-surface-hover',
        outline:
          'bg-transparent text-brand border border-brand hover:bg-info-soft active:bg-info-soft',
        ghost:
          'bg-transparent text-text hover:bg-surface-hover active:bg-surface-hover',
        danger:
          'bg-danger text-brand-fg hover:opacity-90 active:opacity-90 shadow-sm',
        'danger-ghost':
          'bg-transparent text-danger hover:bg-danger-soft active:bg-danger-soft',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-11 px-6 text-base',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
})
